<template>
  <v-container style="max-width: 50rem">
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="text-h4 text-center">
            Frequently Asked Questions
          </v-card-title>
          <v-card-text>
            <div class="mb-6 text-center">
              <p class="text-body-1 text-medium-emphasis">
                Find answers to commonly asked questions about our assessment platform.
              </p>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-text-field
              v-model="searchQuery"
              label="Search FAQs"
              prepend-inner-icon="mdi-magnify"
              clearable
              @input="filterFAQs"
            />
          </v-card-actions>
        </v-card>

        <!-- No Results Message -->
        <v-card v-if="filteredSections.length === 0" class="mt-8">
          <v-card-title class="text-center">
            <v-icon size="64" color="disabled" class="mb-4">
              mdi-help-circle-outline
            </v-icon>
            No results found
          </v-card-title>
          <v-card-text class="text-center">
            Try adjusting your search terms.
          </v-card-text>
          <v-card-actions>
            <v-btn 
              variant="text" 
              color="primary" 
              @click="clearSearch"
            >
              Clear Search
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- FAQ Sections -->
        <v-card-text class="faq-section">
          <div v-for="(section, sectionIndex) in filteredSections" :key="section.title">
            <v-sheet class="mb-6" color="transparent">
              <h2 class="text-h5 mb-4 text-primary font-weight-bold">
                <v-icon class="mr-2" :icon="section.icon" />
                {{ section.title }}
              </h2>
              
              <v-expansion-panels
                v-model="openPanels[sectionIndex]"
                multiple
                variant="accordion"
                class="mb-4"
              >
                <v-expansion-panel
                  v-for="faq in section.faqs"
                  :key="faq.id"
                  :value="faq.id"
                >
                  <v-expansion-panel-title class="text-subtitle-1 font-weight-medium">
                    {{ faq.question }}
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div class="text-body-1 pt-2" v-html="faq.answer" />
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-sheet>
          </div>
        </v-card-text>

        <!-- Contact Section -->
        <v-card>
          <v-card-title class="text-center">
            Can't find the answer you're looking for?
          </v-card-title>
          <v-card-text class="text-center">
            Our support team is here to help.
            <div class="text-center mt-4">
              <v-btn
                to="/contact"
                size="large"
                prepend-icon="mdi-email"
              >
                Contact Support
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue'
import { marked } from 'marked'
import { parseMarkdownToFAQs, type FAQSection } from '~/utils/faqParser'
import faqMarkdown from '~/assets/md/faq.md?raw'

// Page metadata
definePageMeta({
  title: 'FAQ - Frequently Asked Questions'
})

// Reactive data
const searchQuery = ref('')
const openPanels = reactive<number[][]>([])

// Configure marked renderer
const renderer = new marked.Renderer()
renderer.link = ({ href, title, tokens }: { href: string; title?: string | null; tokens: Array<{ raw?: string; text?: string }> }) => {
  const text = tokens.map(token => token.raw || token.text || '').join('')
  return `<a href="${href}" class="text-primary"${title ? ` title="${title}"` : ''}>${text}</a>`
}

marked.setOptions({
  renderer: renderer
})

// Parse FAQ content from markdown
const faqSections = ref<FAQSection[]>([])

onMounted(() => {
  faqSections.value = parseMarkdownToFAQs(faqMarkdown)
  
  // Initialize open panels array
  faqSections.value.forEach((_, index) => {
    openPanels[index] = []
  })
})

// Computed properties
const filteredSections = computed(() => {
  if (!searchQuery.value) return faqSections.value
  
  const query = searchQuery.value.toLowerCase()
  return faqSections.value
    .map(section => ({
      ...section,
      faqs: section.faqs.filter(faq => 
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
      )
    }))
    .filter(section => section.faqs.length > 0)
})

// Methods
const filterFAQs = () => {
  // Reactive computed property handles filtering automatically
}

const clearSearch = () => {
  searchQuery.value = ''
}
</script>

<style scoped>

/* FAQ-specific styling using theme colors */
.faq-section {
  :deep(a) {
    color: rgb(var(--v-theme-primary));
    text-decoration: none;
  }

  :deep(a:hover) {
    text-decoration: underline;
  }

  :deep(ul), :deep(ol) {
    padding-left: 1.25rem;
    margin: 0.5rem 0;
  }

  :deep(li) {
    margin: 0.25rem 0;
    line-height: 1.5;
  }
}

/* Search result highlighting */
.search-highlight {
  background-color: rgba(var(--v-theme-warning), 0.3);
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
}
</style>
